import { useMemo, useState, type DragEvent, type KeyboardEvent } from "react";
import { CalendarDays, Flag, Home, List as ListIcon, MoreHorizontal, Plus, Star, Sun, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { TaskItem, TaskList } from "@/api/mailApi";
import { useCreateTaskList, useDeleteTaskList, useReorderTaskLists, useUpdateTaskList } from "@/api/hooks";
import { inList, SMART_LISTS, type ListKey, type SmartListId } from "./taskModel";

export const TASK_DRAG_TYPE = "application/x-athavita-task";
const LIST_DRAG_TYPE = "application/x-athavita-tasklist";

const SMART_ICON: Record<SmartListId, LucideIcon> = {
    myday: Sun,
    important: Star,
    planned: CalendarDays,
    flagged: Flag,
    tasks: Home
};

const COLORS = ["#0078D4", "#8764B8", "#E3008C", "#D13438", "#CA5010", "#498205", "#038387", "#69797E"];

interface TaskListsPaneProps {
    selected: ListKey;
    onSelect: (key: ListKey) => void;
    tasks: TaskItem[];
    lists: TaskList[];
    today: string;
    /** A task row dropped on a list. */
    onDropTask: (taskId: string, key: ListKey) => void;
    className?: string;
}

/** Left rail of the Tasks page: smart lists, then the user's lists (drag to reorder). */
export function TaskListsPane({ selected, onSelect, tasks, lists, today, onDropTask, className }: TaskListsPaneProps) {
    const createList = useCreateTaskList();
    const reorder = useReorderTaskLists();
    const [newName, setNewName] = useState<string | null>(null);
    const [editing, setEditing] = useState<TaskList | null>(null);
    const [deleting, setDeleting] = useState<TaskList | null>(null);
    const [dropKey, setDropKey] = useState<string | null>(null);

    const counts = useMemo(() => {
        const open = tasks.filter((t) => !t.isCompleted);
        const map = new Map<string, number>();
        for (const s of SMART_LISTS) map.set(s.id, open.filter((t) => inList(t, s.id, today)).length);
        for (const l of lists) map.set(l.id, open.filter((t) => t.listId === l.id).length);
        return map;
    }, [tasks, lists, today]);

    const move = (id: string, beforeId: string | null) => {
        const ids = lists.map((l) => l.id).filter((x) => x !== id);
        const at = beforeId ? ids.indexOf(beforeId) : ids.length;
        ids.splice(at < 0 ? ids.length : at, 0, id);
        reorder.mutate(ids);
    };

    const dropProps = (key: string, listId?: string) => ({
        onDragOver: (e: DragEvent) => {
            const types = e.dataTransfer.types;
            if (!types.includes(TASK_DRAG_TYPE) && !(listId && types.includes(LIST_DRAG_TYPE))) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (dropKey !== key) setDropKey(key);
        },
        onDragLeave: () => { if (dropKey === key) setDropKey(null); },
        onDrop: (e: DragEvent) => {
            setDropKey(null);
            const taskId = e.dataTransfer.getData(TASK_DRAG_TYPE);
            if (taskId) { e.preventDefault(); onDropTask(taskId, key); return; }
            const dragged = e.dataTransfer.getData(LIST_DRAG_TYPE);
            if (dragged && listId && dragged !== listId) { e.preventDefault(); move(dragged, listId); }
        }
    });

    const onListKey = (e: KeyboardEvent, l: TaskList) => {
        if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
        e.preventDefault();
        const i = lists.findIndex((x) => x.id === l.id);
        const ids = lists.map((x) => x.id);
        const j = e.key === "ArrowUp" ? i - 1 : i + 1;
        if (j < 0 || j >= ids.length) return;
        [ids[i], ids[j]] = [ids[j] ?? l.id, ids[i] ?? l.id];
        reorder.mutate(ids);
    };

    const row = (key: string, label: string, icon: React.ReactNode, extra?: React.ReactNode, listId?: string) => {
        const count = counts.get(key) ?? 0;
        const active = selected === key;
        return (
            <div
                key={key}
                className={cn("group flex items-center rounded-md", dropKey === key && "ring-2 ring-primary")}
                {...dropProps(key, listId)}
            >
                <button
                    type="button"
                    onClick={() => onSelect(key)}
                    onKeyDown={listId ? (e) => { const l = lists.find((x) => x.id === listId); if (l) onListKey(e, l); } : undefined}
                    draggable={Boolean(listId)}
                    onDragStart={listId ? (e) => { e.dataTransfer.setData(LIST_DRAG_TYPE, listId); e.dataTransfer.effectAllowed = "move"; } : undefined}
                    aria-current={active ? "page" : undefined}
                    aria-label={`${label}${count ? `, ${count} open` : ""}${listId ? ". Alt+Up or Alt+Down to reorder" : ""}`}
                    className={cn(
                        "flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1.5 text-sm transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        active ? "bg-primary/15 font-semibold" : "hover:bg-foreground/5"
                    )}
                >
                    {icon}
                    <span className="min-w-0 flex-1 truncate text-left">{label}</span>
                    {count > 0 ? <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{count}</span> : null}
                </button>
                {extra}
            </div>
        );
    };

    return (
        <nav className={cn("flex h-full flex-col", className)} aria-label="Task lists">
            <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
                {SMART_LISTS.map((s) => {
                    const Icon = SMART_ICON[s.id];
                    return row(s.id, s.name, <Icon className={cn("h-4 w-4 shrink-0", s.id === "flagged" ? "text-red-500" : "text-primary")} aria-hidden />);
                })}
                {lists.length > 0 ? <div className="my-2 h-px bg-border" /> : null}
                {lists.map((l) =>
                    row(
                        l.id,
                        l.name,
                        l.emoji
                            ? <span className="w-4 shrink-0 text-center text-sm leading-none" aria-hidden>{l.emoji}</span>
                            : <ListIcon className="h-4 w-4 shrink-0" style={{ color: l.color ?? undefined }} aria-hidden />,
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    aria-label={`List options for ${l.name}`}
                                    className="rounded p-1 text-muted-foreground opacity-0 hover:bg-accent focus:opacity-100 group-hover:opacity-100"
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => setEditing(l)}>Rename / icon &amp; colour…</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive" onSelect={() => setDeleting(l)}>Delete list…</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>,
                        l.id
                    )
                )}
            </div>
            <div className="border-t p-2">
                {newName !== null ? (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            const name = newName.trim();
                            if (name) createList.mutate({ name }, { onSuccess: (l) => onSelect(l.id) });
                            setNewName(null);
                        }}
                    >
                        <Input
                            autoFocus
                            value={newName}
                            maxLength={128}
                            onChange={(e) => setNewName(e.target.value)}
                            onBlur={() => setNewName(null)}
                            onKeyDown={(e) => { if (e.key === "Escape") setNewName(null); }}
                            placeholder="List name"
                            aria-label="New list name"
                            className="h-8"
                        />
                    </form>
                ) : (
                    <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-primary" onClick={() => setNewName("")}>
                        <Plus className="h-4 w-4" /> New list
                    </Button>
                )}
            </div>

            {editing ? <EditListDialog list={editing} onClose={() => setEditing(null)} /> : null}
            {deleting ? (
                <DeleteListDialog
                    list={deleting}
                    taskCount={tasks.filter((t) => t.listId === deleting.id).length}
                    onClose={() => setDeleting(null)}
                    onDeleted={() => { if (selected === deleting.id) onSelect("tasks"); }}
                />
            ) : null}
        </nav>
    );
}

function EditListDialog({ list, onClose }: { list: TaskList; onClose: () => void }) {
    const update = useUpdateTaskList();
    const [name, setName] = useState(list.name);
    const [emoji, setEmoji] = useState(list.emoji ?? "");
    const [color, setColor] = useState<string | null>(list.color);
    const save = () => {
        const n = name.trim();
        if (!n) return;
        update.mutate(
            { id: list.id, payload: { name: n, emoji: emoji.trim() || null, color, sortOrder: list.sortOrder } },
            { onSuccess: onClose }
        );
    };
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Edit list</DialogTitle>
                    <DialogDescription>Name, icon and colour.</DialogDescription>
                </DialogHeader>
                <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save(); }}>
                    <div className="flex gap-2">
                        <Input
                            value={emoji}
                            onChange={(e) => setEmoji(Array.from(e.target.value).slice(-2).join(""))}
                            placeholder="😀"
                            aria-label="Emoji"
                            className="w-14 text-center"
                        />
                        <Input value={name} maxLength={128} onChange={(e) => setName(e.target.value)} aria-label="List name" autoFocus />
                    </div>
                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Colour">
                        <button
                            type="button"
                            role="radio"
                            aria-checked={color === null}
                            aria-label="No colour"
                            onClick={() => setColor(null)}
                            className={cn("h-6 w-6 rounded-full border bg-background", color === null && "ring-2 ring-primary ring-offset-2 ring-offset-background")}
                        />
                        {COLORS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                role="radio"
                                aria-checked={color === c}
                                aria-label={c}
                                onClick={() => setColor(c)}
                                style={{ backgroundColor: c }}
                                className={cn("h-6 w-6 rounded-full", color === c && "ring-2 ring-primary ring-offset-2 ring-offset-background")}
                            />
                        ))}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={!name.trim() || update.isPending}>Save</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function DeleteListDialog({ list, taskCount, onClose, onDeleted }: { list: TaskList; taskCount: number; onClose: () => void; onDeleted: () => void }) {
    const remove = useDeleteTaskList();
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Delete “{list.name}”?</DialogTitle>
                    <DialogDescription>
                        {taskCount > 0
                            ? `This list and its ${taskCount} task${taskCount === 1 ? "" : "s"} will be deleted permanently.`
                            : "This list will be deleted permanently."}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button
                        variant="destructive"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(list.id, { onSuccess: () => { onDeleted(); onClose(); } })}
                    >
                        Delete list
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
